/**
 * RepBuddy - Email verification service (SCRUM-49)
 *
 * Same shape, and the same reasoning, as services/passwordResetService.js: it imports
 * nothing from lib/supabaseClient, so the browser never learns which identity provider is
 * behind the verification. Swapping that provider stays a backend change.
 *
 * Both calls are unauthenticated. The account exists at this point but is not allowed a
 * session yet, so there is no token to send - the 6-digit code in the user's inbox is the
 * only credential either endpoint accepts.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

// Shown when the backend is unreachable. Distinct from a backend-supplied error so the user
// can tell "your code was rejected" from "your code never arrived anywhere".
const NETWORK_ERROR = 'We could not reach RepBuddy just now. Check your connection and try again.';

// Both endpoints are public and never answer 401, 403, 404 or 405 on purpose. Getting one
// means the request reached something other than these routes - in practice a backend
// deploy that predates them, during the window where this bundle is newer than the API.
// The server's own text for those ("Missing Authorization header") is meaningless to the
// user, so it is replaced rather than shown.
const UNAVAILABLE_STATUSES = new Set([401, 403, 404, 405]);
const UNAVAILABLE = 'Email verification is temporarily unavailable. Please try again in a few minutes.';

// The statuses whose `error` text these routes write for people: 400 and 429 carrying one of
// our flags, the rate limiter's 429, and the 500/502 recovery messages. Anything else gets
// the caller's generic fallback.
function userFacingError(status, payload, fallback) {
  if (UNAVAILABLE_STATUSES.has(status)) return UNAVAILABLE;
  const flagged = payload.invalid || payload.expired || payload.exhausted;
  if (payload.error && (flagged || status === 429 || status >= 500)) return payload.error;
  return fallback;
}

async function postJson(path, body) {
  const r = await fetch(`${BACKEND_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  // A 429 from the rate limiter and a 502 from a provider failure both arrive as JSON, but
  // a proxy error page might not - hence the guarded parse.
  let payload = {};
  try {
    payload = await r.json();
  } catch {
    payload = {};
  }
  return { ok: r.ok, status: r.status, payload };
}

/**
 * Submit a typed code.
 *
 * The three failure flags are kept distinct all the way to the UI because they call for
 * three different actions: `invalid` means retype, `expired` and `exhausted` both mean the
 * code is dead and the only way forward is a new one.
 *
 * The password travels with the code because redeeming the code is what sets it: the
 * backend only lets whoever holds the inbox decide the account's password.
 *
 * @param {string} email
 * @param {string} code      six digits
 * @param {string} password  the password the account will have once verified
 * @returns {Promise<{
 *   ok: boolean, message: string, alreadyVerified?: boolean,
 *   invalid?: boolean, expired?: boolean, exhausted?: boolean, attemptsLeft?: number,
 * }>}
 */
export async function verifyEmailCode(email, code, password) {
  try {
    const { ok, status, payload } = await postJson('/api/auth/verify-email', { email, code, password });

    if (!ok) {
      return {
        ok: false,
        message: userFacingError(status, payload, 'We could not verify that code. Please try again.'),
        invalid: !!payload.invalid,
        expired: !!payload.expired,
        // Both the attempt ceiling and the per-IP limiter answer 429, so the flag - not the
        // status - is what marks a burnt code. Without that distinction a user who was
        // merely rate-limited would be told to throw away a code that still works.
        exhausted: !!payload.exhausted,
        attemptsLeft: typeof payload.attemptsLeft === 'number' ? payload.attemptsLeft : undefined,
      };
    }

    return {
      ok: true,
      message: payload.message || 'Your email is verified.',
      alreadyVerified: !!payload.alreadyVerified,
    };
  } catch {
    return { ok: false, message: NETWORK_ERROR };
  }
}

/**
 * Ask for a fresh code.
 *
 * Resolves to success unless the network itself failed, and that is not laziness: the
 * endpoint answers identically whether the address has an unverified account, a verified
 * one, or no account at all. Reporting anything finer here would undo that on the client.
 *
 * @param {string} email
 * @returns {Promise<{ ok: boolean, message: string, expiresInMinutes?: number }>}
 */
export async function resendVerificationCode(email) {
  try {
    const { ok, status, payload } = await postJson('/api/auth/resend-verification', { email });

    // The one case worth distinguishing: too many requests from this browser. Saying so is
    // safe - it is a property of the requester, not of the account - and beats a success
    // message that leaves someone waiting for mail that is not coming.
    if (status === 429) {
      return { ok: false, message: payload.error || 'Too many requests. Please wait a few minutes and try again.' };
    }
    if (!ok) {
      return { ok: false, message: userFacingError(status, payload, 'We could not send a new code. Please try again.') };
    }
    return {
      ok: true,
      message: payload.message || 'If that account still needs verifying, a new code is on its way.',
      expiresInMinutes: payload.expiresInMinutes,
    };
  } catch {
    return { ok: false, message: NETWORK_ERROR };
  }
}
